import {Component, effect, inject, input} from '@angular/core';
import {JobsService} from "../../../../services/jobs.service";
import {Router, ActivatedRoute} from '@angular/router';
import {AttachmentType} from "../../../../enums/attachment-type";

@Component({
  selector: 'job-attachments',
  templateUrl: './job-attachments.component.html',
  styleUrls: ['./job-attachments.component.css'],
  standalone: false
})
export class JobAttachmentsComponent {
  jobId = input.required<string>();
  attachments = input.required<any[]>();
  categories: { attachment_type_id: string, attachment_type: string, count: number }[] = [];
  groupedData: any = {};
  filteredGroupedData: any = {};
  selectedCategoryId: string | null = null;
  selectedCategory: { attachment_type_id: string, attachment_type: string, count: number } = {
    attachment_type_id: '0',
    attachment_type: 'Default Category',
    count: 0
  };
  search: string = "";
  readonly itemsPerPage: number = 5;
  totalItems: number = -1;
  totalCount: number = 0;
  outgoingAssetPhotosCount: number = 0;
  jobPaperworkCount: number = 0;
  incomingAssetPhotosCount: number = 0;
  customerInfoCount: number = 0;

  private readonly jobService = inject(JobsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  constructor() {
    effect(() => {
      const attachments = this.attachments();
      this.groupData(attachments);
      if (attachments.length > 0) {
        this.totalCount = attachments[0].total_count;
        this.outgoingAssetPhotosCount = attachments[0].outgoing_asset_photos_count;
        this.jobPaperworkCount = attachments[0].job_paperwork_count;
        this.incomingAssetPhotosCount = attachments[0].incoming_asset_photos_count;
        this.customerInfoCount = attachments[0].customer_info_count;
      }

      this.extractCategories(attachments);
      if (this.categories.length > 0) {
        this.filterByCategory(this.categories[0]);
        this.selectedCategoryId = this.categories[0].attachment_type_id;
      }
    });
  }

  async getJobPaperWorkAttachmentsWithPagination(job_id: string, attachment_type_id: string, search: string, limit: number, offset: number): Promise<any[]> {
    return this.jobService.getJobAttachmentsWithPagination(job_id, attachment_type_id, search, limit, offset).then((data: any) => {
      if (!data || !Array.isArray(data.Data) || data.Data.length === 0) {
        this.totalItems = 0;
        return [];
      }
      this.totalItems = data.Data[0].total_count;
      const groupId = AttachmentType.JobPaperwork;   //update only PaperWork group, leave other groups unchanged
      this.groupedData[groupId] = [];
      data.Data.forEach((item: any) => {
        this.groupedData[groupId].push(item);
      });
      return data.Data;
    });
  }

  async filterPaperWorkSearchChanged() {
    const results = await this.getJobPaperWorkAttachmentsWithPagination(this.jobId(), this.selectedCategoryId!, this.search, this.itemsPerPage, 0);
    if (this.totalItems > 5 && this.search !== "") {  //if result is more than 5 items redirect to details page
      const job_id = this.route.snapshot.queryParams['job_id'];
      const urlTree = this.router.createUrlTree(['/job-attachments-details'], {
        queryParams: {
          job_id: job_id,
          attachment_type_id: this.selectedCategoryId,
          search: this.search
        }
      });
      window.open(urlTree.toString(), '_blank');
    } else {
      this.filteredGroupedData = results.reduce((res: any, item: any) => {
        const groupId = AttachmentType.JobPaperwork;   //update only PaperWork group, leave other groups unchanged
        if (!res[groupId]) {
          res[groupId] = [];
        }
        res[groupId].push(item);
        return res;
      }, {});
    }
  }

  extractCategories(data: any[]) {
    const categoryCounts: { [key: string]: number } = {
      [AttachmentType.JobPaperwork]: this.jobPaperworkCount,
      [AttachmentType.IncomingAssetPhotos]: this.incomingAssetPhotosCount,
      [AttachmentType.OutgoingAssetPhotos]: this.outgoingAssetPhotosCount,
      [AttachmentType.CustomerInfo]: this.customerInfoCount
    };

    const uniqueCategories = new Map<string, { attachment_type: string, count: number }>();

    data.forEach(item => {
      if (!uniqueCategories.has(item.attachment_type_id)) {
        uniqueCategories.set(item.attachment_type_id, {
          attachment_type: item.attachment_type,
          count: categoryCounts[item.attachment_type_id] || 0
        });
      }
    });

    this.categories = Array.from(uniqueCategories, ([attachment_type_id, category]) => ({attachment_type_id, ...category}));
  }

  private groupData(data: any[]) {
    this.groupedData = {};
    data.forEach(item => {
      let groupId = item.attachment_type_id;
      if (!this.groupedData[groupId]) {
        this.groupedData[groupId] = [];
      }
      this.groupedData[groupId].push(item);
    });
  }

  filterByCategory(category: { attachment_type_id: string, attachment_type: string, count: number } | null) {
    if (category) {
      this.selectedCategory = category;
      this.selectedCategoryId = category.attachment_type_id;
      this.filteredGroupedData = Object.keys(this.groupedData)
        .filter(key => this.groupedData[key].some((item: any) => item.attachment_type_id === category.attachment_type_id))
        .reduce((res: any, key) => {
          res[key] = this.groupedData[key];
          return res;
        }, {});
    } else {
      this.selectedCategoryId = null;
      this.filteredGroupedData = this.groupedData;
    }
  }

  getGroupKeys(groupedData: any): any {
    return Object.keys(groupedData);
  }

  handleShowMore(attachmentTypeId: any) {
    const jobId = this.route.snapshot.queryParams['job_id'];
    const url = this.router.createUrlTree(['/job-attachments-details'], {
      queryParams: {
        job_id: jobId,
        attachment_type_id: attachmentTypeId
      }
    }).toString();
    window.open(url, '_blank');
  }
}
